import * as React from 'react';
import { LayoutChangeEvent, View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  doReportingClosePage,
  doReportingLoad,
  doReportingOpenPage,
  doReportingSelectReportOpen,
  doToggleIsSidebarVisible,
} from 'store/actions';
import { useSelector } from 'hooks/useSelector';
import { selectRptReportPages } from 'store/remote/selectors';
import { ICONS } from 'components/Icon';
import { PageScrollView } from 'components/Page';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { NavigationHeader } from 'components/NavigationHeader';
import { theme } from 'lib/theme';
import { Logo } from 'components/Logo';
import { IconButton } from 'components/IconButton';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { Menu, MenuProps } from 'components/Menu';
import { ScrollView } from 'components/ScrollView';
import { UserAvatar } from 'components/UserAvatar';
import { ItemDetails } from 'components/ItemDetails';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { selectUserEmail, selectUserName } from 'store/auth/selectors';
import { ResizableSidebar } from '../../components/ResizableSidebar';
import { Button, ButtonTheme } from 'components/Button';
import { useDrawerStatus } from '@react-navigation/drawer';
import { useScreenSize } from 'hooks/useScreenSize';
import { ScreenSize } from 'lib/constants';
import { DrawerActions } from '@react-navigation/native';
import { navigationRef } from 'components/Navigation/navigationRef';

export const ReportingNavigation: React.FC<{
  onLayout: (event: LayoutChangeEvent) => void;
  width: number;
  resizable: boolean;
  forceResizable: boolean;
}> = ({ onLayout, width, resizable, forceResizable }) => {
  const dispatch = useDispatch();
  const reportPages = useSelector(selectRptReportPages);
  const hasReportPages = !!reportPages.length;
  const screenSize = useScreenSize();
  const drawerStatus = useDrawerStatus();
  const userName = useSelector(selectUserName);
  const userEmail = useSelector(selectUserEmail);
  const [logoWidth, setLogoWidth] = React.useState(0);
  const handleLogoLayout = (event: LayoutChangeEvent) => {
    if (event.nativeEvent.layout.width) {
      setLogoWidth(Math.min(event.nativeEvent.layout.width, 100));
    }
  };

  const Children = React.useMemo(
    () => (
      <View style={{ flex: 1, backgroundColor: '#F3F2F8' }}>
        <PageScrollView
          header={
            <NavigationHeader marginLeft={0} marginRight={0}>
              <View
                style={{
                  flex: 1,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                {screenSize !== ScreenSize.small && (
                  <IconButton
                    icon={ICONS.SidebarLight}
                    onPress={() => {
                      dispatch(doToggleIsSidebarVisible());
                      navigationRef.current?.dispatch(
                        DrawerActions.closeDrawer(),
                      );
                    }}
                    disabled={!hasReportPages}
                  />
                )}
                <View
                  style={{
                    flex: 1,
                    marginHorizontal: theme.paddingHorizontalHalf,
                    alignItems:
                      screenSize === ScreenSize.small ? 'flex-start' : 'center',
                  }}
                  onLayout={handleLogoLayout}
                >
                  <Logo width={logoWidth} />
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Menu
                    iconButtonConfig={{
                      icon: ICONS.EllipsisLight,
                    }}
                    options={[
                      {
                        text: 'Open A Page',
                        onPress: () => dispatch(doReportingSelectReportOpen()),
                        icon: ICONS.PlusLight,
                      },
                      '-',
                      {
                        text: 'Refresh Pages List',
                        onPress: () => dispatch(doReportingLoad()),
                        icon: ICONS.RotateRightLight,
                      },
                    ]}
                  />
                </View>
              </View>
            </NavigationHeader>
          }
          isInModal={false}
          contentPaddingTop={0}
          contentPaddingBottom={0}
        >
          <ViewWithSeparators
            separatorMargin={theme.paddingHorizontal}
            style={{ flex: 1, paddingBottom: theme.paddingPageVertical }}
          >
            <ScrollView
              contentContainerStyle={{ paddingTop: theme.paddingPageVertical }}
            >
              <ViewWithSeparators
                separatorMargin={1 / 2}
                separatorColor="transparent"
              >
                {!!reportPages.length &&
                  reportPages.map((page) => {
                    const menuItems: MenuProps['options'] = [
                      {
                        text: 'Open A Page',
                        onPress: !page.current
                          ? () =>
                              dispatch(doReportingOpenPage({ key: page.key }))
                          : undefined,
                      },
                    ];
                    if (page.closeable) {
                      menuItems.push({
                        text: 'Close Page',
                        onPress: () =>
                          dispatch(doReportingClosePage({ key: page.key })),
                        destructive: true,
                      });
                    }
                    return (
                      <Card key={page.key} margin={false} withShadow={false}>
                        <CardRow
                          key={page.key}
                          label={page.title}
                          labelWeight="regular"
                          labelSize="xsmall"
                          style={{
                            backgroundColor: page.current
                              ? theme.colors.blue10
                              : 'transparent',
                            minHeight: 0,
                          }}
                          hoveredStyle={{
                            backgroundColor: theme.colors.blue10,
                          }}
                          selected={page.current}
                          onPress={() =>
                            dispatch(doReportingOpenPage({ key: page.key }))
                          }
                          menuOptions={menuItems}
                          hideMenuButton
                          // compact
                          small
                        />
                      </Card>
                    );
                  })}
              </ViewWithSeparators>
            </ScrollView>
          </ViewWithSeparators>
        </PageScrollView>
        <SeparatorHorizontal />
        <View
          style={{
            paddingHorizontal: theme.paddingHorizontal,
            paddingVertical: theme.paddingHorizontal,
          }}
        >
          <Button
            text="Open Page"
            onPress={() => dispatch(doReportingSelectReportOpen())}
            icon={ICONS.PlusLight}
            buttonTheme={ButtonTheme.themeless}
          />
        </View>
        <SeparatorHorizontal />
        <ViewWithSeparators
          separatorMargin={theme.paddingHorizontalHalf / 2}
          separatorColor="transparent"
          style={{ paddingVertical: theme.paddingHorizontal }}
        >
          <Card margin={false} withShadow={false}>
            <CardRow
              onPress={() => dispatch(doReportingLoad())}
              style={{
                backgroundColor: 'transparent',
              }}
              hoveredStyle={{
                backgroundColor: theme.colors.blue10,
              }}
              childrenStyle={{
                alignItems: 'center',
                flexDirection: 'row',
                justifyContent: 'center',
              }}
              compact
              disabled
            >
              <UserAvatar
                size={31}
                style={{ marginRight: theme.paddingHorizontal }}
                previewOnPress={false}
              />
              <ItemDetails
                title={userName || ''}
                subtitle={userEmail || ''}
                titleColor={theme.colors.textBlack64}
                subtitleColor={theme.colors.textBlack64}
                small
              />
            </CardRow>
          </Card>
        </ViewWithSeparators>
      </View>
    ),
    [
      screenSize,
      hasReportPages,
      logoWidth,
      reportPages,
      userName,
      userEmail,
      dispatch,
    ],
  );

  return forceResizable || (resizable && drawerStatus === 'open') ? (
    <ResizableSidebar
      startWidth={width}
      minWidth={190}
      maxWidth={400}
      onLayout={onLayout}
    >
      {Children}
    </ResizableSidebar>
  ) : (
    Children
  );
};
